<template>

  <page-header :title="`License #${accessCode?.code}`">
    <template #actions>
      <v-btn
        variant="outlined"
        class="bg-white mr-2"
        prepend-icon="far fa-copy"
        @click="copyCode()"
      >
        Copy Code
      </v-btn>
      <v-menu
        v-if="isAdminOrRoot"
        bottom
        transition="scale-transition"
        prepend-icon="fa fa-bars"
      >
        <template v-slot:activator="{ props }">
          <v-btn
            variant="outlined"
            class="bg-white"
            v-bind="props"
            data-pw="menuButton"
            prepend-icon="fa fa-cog"
          >
            Settings
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            :disabled="!canEdit"
            data-pw="changeAccountType"
            @click="showChangeAccountType = true"
          >
            {{ $t("components.AccessCodeDetailPage.change_account_type") }}
          </v-list-item>
          <v-list-item
            :disabled="!canEdit"
            data-pw="changeResearchAssessments"
            @click="showChangeResearch = true"
          >
            {{ $t("components.AccessCodeDetailPage.change_research_assessments") }}
          </v-list-item>
          <v-list-item
            :disabled="!canEdit"
            data-pw="deleteVoucher"
            class="text-red"
            @click="showDeleteVoucher = true"
          >
            {{ $t("components.AccessCodeDetailPage.delete_voucher") }}
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
  </page-header>

  <v-container fluid class="py-4">
    <v-row>
      <v-col cols="9">
        <AccessCodeDetailsCard
          data-pw="voucherCardTemplate"
          v-if="accessCode"
          :access-code="accessCode"
        />
        <AccessCodeUserDetailsCard
          v-if="accessCode"
          :user-id="accessCode?.user as string"
          class="mt-6"
        />
      </v-col>

      <v-col>
        <v-card variant="outlined" class="bg-white pa-3">
          <v-card-title>
            Voucher Status
          </v-card-title>
          <v-card-text>
            <v-row class="align-center">
              <v-col cols="auto">Status</v-col>
              <v-spacer />
              <v-col cols="auto">
                <v-chip size="small" :color="accessCode?.usedAt ? 'green' : 'gray'" class="font-weight-bold">
                  {{ accessCode?.usedAt ? 'Used' : 'Not Used' }}
                </v-chip>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-alert v-if="accessCode?.usedAt" type="success" variant="outlined">
                  This voucher has been redeemed
                </v-alert>
                <v-alert v-else type="info" variant="outlined">
                  This voucher has not been redeemed
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <AccessCodeTenantDetailsCard
          v-if="accessCode && accessCode.tenant"
          :access-code="accessCode"
          class="mt-6"
        />
      </v-col>

    </v-row>

    <v-dialog v-model="showChangeAccountType" :persistent="true" max-width="500">
      <ChangeAccountType
        @close="onCloseEditAccountTypeDialog"
        :title="$t('components.AccessCodeDetailPage.change_account_type')"
        :user-choice="true"
        data-pw="changeAccountTypeDialog"
      />
    </v-dialog>
    <v-dialog v-model="showDeleteVoucher" persistent max-width="500">
      <delete-voucher
        @close="onCloseDeleteVoucherDialog"
        :title="$t('components.AccessCodeDetailPage.delete_voucher')"
        :user-choice="true"
        :confirm-string="accessCode?.code!"
        :message="$t('components.AccessCodeDetailPage.message_warning')"
        data-pw="deleteVoucherDialog"
      />
    </v-dialog>
    <v-dialog v-model="showChangeResearch" persistent max-width="500">
      <change-research
        v-if="accessCode"
        @close="onCloseChangeResearchDialog"
        :title="$t('components.AccessCodeDetailPage.change_research_assessments')"
        :user-choice="true"
        data-pw="changeResearchDialog"
        :access-code="accessCode"
      ></change-research>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, defineExpose} from "vue";
import {AccessCode, isYapiError, Role, YapiError} from "@YenzaCT/sdk";

import yapi from "@/lib/yapi";
import {useGlobalStore} from "@/store";
import ChangeAccountType from "@/components/Voucher/ChangeAccountType.vue";
import deleteVoucher from "@/components/Voucher/DeleteVoucher.vue";
import changeResearch from "@/components/Voucher/ChangeResearchAssessment.vue";
import AccessCodeDetailsCard from "@/pages/Licenses/AccessCodeDetailPage/AccessCodeDetailsCard.vue";
import AccessCodeTenantDetailsCard from "@/pages/Licenses/AccessCodeDetailPage/AccessCodeTenantDetailsCard.vue";
import AccessCodeUserDetailsCard from "@/pages/Licenses/AccessCodeDetailPage/AccessCodeUserDetailsCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import {useRoute} from "vue-router";

const emit = defineEmits<{
  accessCodeLoaded: [accessCode: AccessCode];
  copyCode: [];
  changeAccountType: [];
  deleteVoucher: [];
  changeResearch: [];
}>();

const route = useRoute();

const store = useGlobalStore();
const accessCode = ref<AccessCode>();

const accessCodeId = route.params.id as string;

const isAdminOrRoot = ref(store.user?.app.role === Role.Admin || store.user?.app.role === Role.Root);
const showChangeAccountType = ref(false);
const showDeleteVoucher = ref(false);
const showChangeResearch = ref(false);

const canEdit = computed(() => accessCode.value?.usedAt === undefined);

async function fetchAccessCode() {
  try {
    store.networkBusy = true;
    accessCode.value = (await yapi.admin.accessCode.get(accessCodeId)).data;
    emit("accessCodeLoaded", accessCode.value);
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    store.networkBusy = false;
  }
}

async function copyCode() {
  if (accessCode.value?.code) {
    try {
      await navigator.clipboard.writeText(accessCode.value.code);
      store.showSnackbar("Code copied to clipboard");
      emit("copyCode");
    } catch (e) {
      store.showSnackbar("Error copying code.");
    }
  }
}

onMounted(async () => {
  await fetchAccessCode();
});

async function onCloseEditAccountTypeDialog() {
  showChangeAccountType.value = false;
  await fetchAccessCode();
  emit("changeAccountType");
}

async function onCloseDeleteVoucherDialog() {
  showDeleteVoucher.value = false;
  await fetchAccessCode();
  emit("deleteVoucher");
}

async function onCloseChangeResearchDialog() {
  showChangeResearch.value = false;
  await fetchAccessCode();
  emit("changeResearch");
}

defineExpose({
  copyCode,
  showChangeAccountType: () => { showChangeAccountType.value = true; },
  showDeleteVoucher: () => { showDeleteVoucher.value = true; },
  showChangeResearch: () => { showChangeResearch.value = true; },
  canEdit,
  accessCode
});
</script>
